import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import GuestWishlist from '~/backend/guest_wishlist';
import ShopCart from '~/backend/shop_cart';
import UserSession from '~/backend/user_session';
import {
	calculateProductDiscount,
	calculateProductPrice,
	calculateProductPriceBD,
	calculateProductPriceValue,
	getEntityImages
} from '~/common/helper';
import { sendAddedToCartKlaviyoEvent } from '~/common/klaviyo.js';
import { notifyError, notifyInfo } from '~/components/common/ToastMessages';
import noimage_png from '~/images/no-image.png';
import { addToCartAction, addToWishlistAction, openCartWidgetAction } from '~/store/actions.js';

import { sendAddToCartFacebookEvent } from '~/common/facebookPixel.js';
import { sendAddToCartGTAG4Event } from '~/common/gtag4.js';
import { sendAddToCartGTMEvent, sendSelectItemGTMEvent } from '~/common/gtm.js';
import { sendAddToCartTiktokEvent } from '~/common/tiktokPixel.js';

export default function ProductBox(props) {
	// Do NOT declare constant / let, except for state variables because their value will not be updated correctly when we re-render due to filter/sorting etc.
	const [sizes, setSizes] = useState([]);
	const [chosenColor, setChosenColor] = useState('');
	const [uniqueColors, setUniqueColors] = useState([]);
	const [wishlistActive, setWishlistActive] = useState(false);
	const [firstImage, setFirstImage] = useState(noimage_png);
	const [secondImage, setSecondImage] = useState(noimage_png);
	const [price, setPrice] = useState(0);
	const [priceBD, setPriceBD] = useState(0);
	const [discount, setDiscount] = useState(0);

	const dispatch = useDispatch();
	const { t } = useTranslation();

	useEffect(() => {
		const uniqueColors = [...new Map(props.product.dimensions.filter(item => item.cval).map(item => [item['cval'], item])).values()];

		const sortedByDscUniqueColors = uniqueColors.slice().sort((a, b) => {
			if (a.dsc === undefined) return 1; // Move undefined values to the end
			if (b.dsc === undefined) return -1; // Move undefined values to the end
			return b.dsc - a.dsc;
		});
		setUniqueColors(sortedByDscUniqueColors);

		const defaultImages = getEntityImages(props.product || {});
		setFirstImage(defaultImages.at(0) ?? noimage_png);
		setSecondImage(defaultImages.at(1));

		// Check if we need to set wish list via product property and then check guest wish list below...
		setWishlistActive(props.product.inwishlist);

		if (!UserSession.isAuthenticated()) {
			setWishlistActive(GuestWishlist.find(props.product.id));
		}
	}, [props.product.id]);

	useEffect(() => {
		if (uniqueColors.length > 0) {
			const firstColor = uniqueColors[0];
			const dimensionsIds = props.product.dimensions.filter(x => x.cval == firstColor.cval).map(y => y.id);
			const sizes = props.product.dimensions.filter(x => x.cval == null && dimensionsIds.includes(x.id));
			setChosenColor(firstColor.cval);
			setSizes(sizes);
			setColorPictures(sizes, firstColor);
		} else {
			setSizes(props.product.dimensions);
		}
	}, [uniqueColors]);

	useEffect(() => {
		let dimensionId = 0;

		if (chosenColor) {
			dimensionId = props.product.dimensions.find(x => x.cval == chosenColor)?.id;
		}

		let price = calculateProductPrice(props.product, dimensionId);
		let priceBD = calculateProductPriceBD(props.product, dimensionId);
		let discount = calculateProductDiscount(props.product, dimensionId);

		setPrice(price);
		setPriceBD(priceBD);
		setDiscount(discount);
	}, [props.product.id, chosenColor]);

	const handleColorChange = color => {
		const dimensionsIds = props.product.dimensions.filter(x => x.cval == color).map(y => y.id);
		const sizes = props.product.dimensions.filter(x => x.cval == null && dimensionsIds.includes(x.id));

		setSizes(sizes);
		setChosenColor(color);
		setColorPictures(sizes, color);
	};

	const setColorPictures = (sizes, color) => {
		//if (color === [...uniqueColors][0]) {
		//    setFirstImage(props.product.img1);
		//    setSecondImage(props.product.img2);
		//    return;
		//}

		let imgArray = [];
		for (let i = 0; i < sizes.length; i++) {
			let size = sizes[i];
			imgArray = getEntityImages(size);
			if (imgArray.length > 1) break;
		}

		if (imgArray.length > 1) {
			setFirstImage(imgArray[0]);
			setSecondImage(imgArray[1]);
		} else if (imgArray.length === 1) {
			setFirstImage(imgArray[0]);
			setSecondImage(null);
		}
	};

	const handleAddToCart = item => {
		const price = calculateProductPriceValue(props.product, item.id);

		if (!price || price <= 0) {
			notifyError(t('zero_price'));
			return;
		}

		dispatch(
			addToCartAction({
				pid: props.product.id,
				dimension: props.product.dimensions.length == 0 ? 0 : item.id,
				quantity: 1,
				product: props.product
			})
		);

		sendAddedToCartKlaviyoEvent(UserSession.getEmail(), props.product, ShopCart.getItems());

		if (window.innerWidth > 900) {
			dispatch(openCartWidgetAction(true));

			document.body.classList.add('cart-open');

			setTimeout(() => {
				document.body.classList.remove('cart-open');
				dispatch(openCartWidgetAction(false));
			}, 2000);
		}

		notifyInfo(t('added_to_cart'));
		sendAddToCartGTAG4Event(props.product, item.id, 1);
		sendAddToCartFacebookEvent(props.product);
		sendAddToCartTiktokEvent(props.product);
		sendAddToCartGTMEvent(props.product, item.id, 1);
	};

	const handleWishlistTrigger = () => {
		if (UserSession.isAuthenticated()) {
			dispatch(
				addToWishlistAction({
					productId: props.product.id
				})
			);
			document.body.classList.add('overflow');
		} else {
			let status = GuestWishlist.addOrRemove(props.product);
			let message = '';

			if (status === 'added') {
				message = t('product_added_to_wishlist_guest');
				setWishlistActive(true);
			} else {
				message = t('product_removed_from_wishlist_guest');
				setWishlistActive(false);
			}

			notifyInfo(message);
		}
	};

	const showColors = dimensions => {
		if (dimensions != null && dimensions.length > 1) {
			return uniqueColors.map((color, index) => (
				<div key={index}>
					<input
						type="checkbox"
						onChange={() => handleColorChange(color.cval)}
						checked={color.cval == chosenColor}
						style={{
							backgroundColor: color.chex
						}}
					/>
				</div>
			));
		}
		return null;
	};

	const showSizes = sizes => {
		if (sizes != null && sizes.length > 0) {
			sizes.sort((a, b) => a.si - b.si);
			return sizes.map((item, index) => (
				<button
					type="button"
					disabled={!item.qty || item.qty <= 0}
					onClick={() => handleAddToCart(item)}
					key={props.product.id + '-' + index}>
					{item.svtxt}
				</button>
			));
		}
		return null;
	};

	const showPrice = () => {
		return (
			<div>
				{/*{priceBD && <span>{priceBD}</span>}*/}
				{price}
			</div>
		);
	};

	const showDiscount = () => {
		return (
			<>
				<div className="discount-container">
					<span className="discount-tag">Top Price</span>
				</div>
			</>
		);
	};

	const showStatus = () => {
		let statuses = props.product.stslst?.filter(x => x.iv === true);
		if (!statuses || statuses.length === 0) return null;

		statuses.sort((a, b) => a.si - b.si);

		// Remove accents/diacritics so that uppercase works well
		statuses = statuses.map(x => x.snm.normalize('NFD').replace(/[\u0300-\u036f]/g, ''));

		return (
			<div className="product-statuses">
				{statuses.map(status => (
					<span key={status}>{status}</span>
				))}
			</div>
		);
	};

	return (
		<div className="product-box" id={props.product.id}>
			<button type="button" className="add-wish" onClick={() => handleWishlistTrigger()}>
				<svg
					className={wishlistActive ? 'active' : ''}
					xmlns="http://www.w3.org/2000/svg"
					width="14.646"
					height="12.902"
					viewBox="0 0 14.646 12.902">
					<path
						d="M14.917,5.549a3.59,3.59,0,0,0-5.079,0l-.692.692-.692-.692a3.591,3.591,0,1,0-5.079,5.079l.692.692L9.146,16.4l5.079-5.079.692-.692a3.59,3.59,0,0,0,0-5.079Z"
						transform="translate(-1.823 -3.997)"
						fill="none"
						stroke="#000"
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth="1"
					/>
				</svg>
			</button>
			<div className="prod-img">
				{discount > 0 && showDiscount()}
				<Link to={props.product.eurl} onClick={() => sendSelectItemGTMEvent(props.product, props.idx, props.listName)}>
					<img src={firstImage} alt={props.product.nm} width="282" height="385" loading="lazy" style={{ width: '100%', height: 'auto' }} />
					{secondImage ? (
						<img className="on-hover" src={secondImage} alt={props.product.nm} width="282" height="385" loading="lazy" />
					) : null}
				</Link>
				{sizes.length > 0 ? (
					<div className="quick-buy btn-hover flex-col">
						<span>Quick buy</span>
						<div className="prod-dim flex-col">{showSizes(sizes)}</div>
					</div>
				) : (
					<button type="button" onClick={() => handleAddToCart(props.product)} className="quick-buy flex-col">
						<span>Quick buy</span>
					</button>
				)}
			</div>

			<div className="prod-details">
				{showStatus()}
				<div className="flex-col">
					<div>
						<h4>{props.product.nm}</h4>
						<div className="prod-colors flex-col">{showColors(props.product.dimensions)}</div>
					</div>

					<div className="prod-price">{showPrice()}</div>
				</div>
			</div>
		</div>
	);
}

ProductBox.propTypes = {
	product: PropTypes.object,
	listName: PropTypes.any
};
